import React, { useState } from 'react';
import { Button, TextButton, Tooltip, Text, Link } from '@amzn/storm-ui';
import { FieldValues, useFormContext } from 'react-hook-form';
import useIntl from '@i18n/useIntl';
import { logEvent } from '@utils/takt';
import { getSelectedSellingAccount } from '@features/account-registration/utils';
import {
  SELLING_ACCOUNTS_CONTROLLER_GROUP,
  SELECTED_SELLING_ACCOUNT_ID,
} from '@features/account-settings/constants';
import { VENDOR_CODE_SELECTION_SUBMIT } from 'src/pages/retailerAccountRegistration/constants';
import CaptchaModal from '@components/modals/CaptchaModal';
import ConfirmationModal from '@components/modals/ConfirmationModal';
import { Captcha, CaptchaResponse } from '@models/captcha';
import * as formFooterConst from './constants';

import {
  RegistrationPageFooterWrapper,
  RegistrationPageFooterActions,
  RegistrationPageFooterText,
} from './style';
import {
  REGISTRATION_FORM_FOOTER_MESSAGE_ADS_AGREEMENT_LINK_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_MESSAGE_DSP_AGREEMENT_LINK_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_MESSAGE_DSP_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_MESSAGE_TRANSLATION_KEY,
} from './constants';
import { DynamicText } from 'src/shared/components/DynamicText';

interface RegistrationFormFooterProps {
  id: string;
  captcha?: Partial<Captcha>;
  onSubmit: (data: FieldValues, captchaResponse?: CaptchaResponse) => void;
  isTermsHidden?: boolean;
  isDspRegistration?: boolean;
}

const RegistrationFormFooter = ({
  id,
  captcha,
  onSubmit,
  isTermsHidden,
  isDspRegistration,
}: RegistrationFormFooterProps) => {
  const { intl } = useIntl();
  const { formState, handleSubmit, getValues } = useFormContext();
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isValid = formState.isValid;

  const logVendorCodeSelection = () => {
    const sellingAccount = getSelectedSellingAccount(
      getValues(SELLING_ACCOUNTS_CONTROLLER_GROUP) ?? [],
      getValues(SELECTED_SELLING_ACCOUNT_ID),
    );
    if (sellingAccount) {
      logEvent(`${id}-${VENDOR_CODE_SELECTION_SUBMIT}`, {
        portals: sellingAccount.portals,
      });
    }
  };

  const submitHandler = async () => {
    if (captcha && captcha?.captchaEnabled) {
      setIsCaptchaOpen(true);
    } else {
      setIsCaptchaOpen(false);
      await handleSubmit((data) => {
        logVendorCodeSelection();
        onSubmit(data);
      })();
    }
  };

  const registrationCancellationHandler = () => {
    logEvent(`${id}-${formFooterConst.CANCEL_BUTTON}`);
    history.back();
  };

  const linkConfig = {
    amazonAdsTermsLink: {
      link: formFooterConst.TERMS_PAGE_URL,
      translationKey:
        REGISTRATION_FORM_FOOTER_MESSAGE_ADS_AGREEMENT_LINK_TRANSLATION_KEY,
    },
    amazonDspTermsLink: {
      link: formFooterConst.DSP_TERMS_PAGE_URL,
      translationKey:
        REGISTRATION_FORM_FOOTER_MESSAGE_DSP_AGREEMENT_LINK_TRANSLATION_KEY,
    },
  };
  return (
    <RegistrationPageFooterWrapper>
      {isTermsHidden ? null : (
        <RegistrationPageFooterText>
          <DynamicText
            message={
              isDspRegistration
                ? intl.getMessage(
                    REGISTRATION_FORM_FOOTER_MESSAGE_DSP_TRANSLATION_KEY,
                  )
                : intl.getMessage(
                    REGISTRATION_FORM_FOOTER_MESSAGE_TRANSLATION_KEY,
                  )
            }
            resolveComponent={(part) => {
              const partConfig = linkConfig[part as keyof typeof linkConfig];
              return (
                <Link href={partConfig.link}>
                  {intl.getMessage(partConfig.translationKey)}
                </Link>
              );
            }}
          />
        </RegistrationPageFooterText>
      )}
      <RegistrationPageFooterActions>
        <TextButton
          id={`${id}-${formFooterConst.CANCEL_BUTTON}`}
          onClick={() => setIsModalOpen(true)}
        >
          {intl.getMessage(
            formFooterConst.REGISTRATION_FORM_FOOTER_CANCEL_BUTTON_TRANSLATION_KEY,
          )}
        </TextButton>
        {!isValid && (
          <Tooltip
            trigger={
              <div>
                <Button
                  variant="primary"
                  disabled={!isValid}
                  id={`${id}-${formFooterConst.SUBMIT_BUTTON}`}
                >
                  {intl.getMessage(
                    isTermsHidden
                      ? formFooterConst.ADSP_REGISTRATION_FORM_FOOTER_SUBMIT_BUTTON_TRANSLATION_KEY
                      : formFooterConst.REGISTRATION_FORM_FOOTER_SUBMIT_BUTTON_TRANSLATION_KEY,
                  )}
                </Button>
              </div>
            }
            message={intl.getMessage(
              formFooterConst.REGISTRATION_FORM_FOOTER_TOOLTIP_MESSAGE_TRANSLATION_KEY,
            )}
            variant="inverted"
          />
        )}
        {isValid && (
          <Button
            id={`${id}-${formFooterConst.SUBMIT_BUTTON}`}
            taktId={`${id}-${formFooterConst.SUBMIT_BUTTON}`}
            variant="primary"
            onClick={async () => {
              await submitHandler();
            }}
          >
            {intl.getMessage(
              formFooterConst.REGISTRATION_FORM_FOOTER_SUBMIT_BUTTON_TRANSLATION_KEY,
            )}
          </Button>
        )}
      </RegistrationPageFooterActions>
      <CaptchaModal
        id={`${id}-${formFooterConst.CAPTCHA_MODAL}`}
        isOpen={isCaptchaOpen}
        captchaOptions={{
          ...captcha,
          'data-event': new CustomEvent('customEvent'),
          'data-iframe-id': `${id}-${formFooterConst.CHALLENGE_IFRAME}`,
          'data-modal': false,
          'data-callback': async (captchaResponse: CaptchaResponse) => {
            if (captchaResponse) {
              setIsCaptchaOpen(false);
              await handleSubmit((formData) => {
                logVendorCodeSelection();
                onSubmit(formData, captchaResponse);
              })();
            }
          },
        }}
      />
      <ConfirmationModal
        id={formFooterConst.CANCEL_REGISTRATION_CONFIRM_MODAL_ID}
        header={
          <Text size={'large'}>
            {intl.getMessage(
              formFooterConst.CANCEL_REGISTRATION_CONFIRMATION_MODAL_HEADER_TRANSLATION_KEY,
            )}
          </Text>
        }
        isOpen={isModalOpen}
        cancelButtonText={intl.getMessage(
          formFooterConst.CANCEL_REGISTRATION_CONFIRMATION_MODAL_CANCEL_BUTTON_TRANSLATION_KEY,
        )}
        confirmButtonText={intl.getMessage(
          formFooterConst.CANCEL_REGISTRATION_CONFIRMATION_MODAL_CONFIRM_BUTTON_TRANSLATION_KEY,
        )}
        onConfirm={registrationCancellationHandler}
        onCancel={() => setIsModalOpen(false)}
      >
        <Text size={'base'}>
          {intl.getMessage(
            formFooterConst.CANCEL_REGISTRATION_CONFIRMATION_MODAL_MESSAGE_TRANSLATION_KEY,
          )}
        </Text>
      </ConfirmationModal>
    </RegistrationPageFooterWrapper>
  );
};

export default RegistrationFormFooter;
